import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { Tenant } from '../../lib/types.js';
import type { PackageAssignment, PackageCatalog } from './types.js';

export const usePackageCatalog = () =>
  useQuery({
    queryKey: queryKeys.servicePackages(),
    queryFn: () => apiRequest<PackageCatalog>('/api/service-packages'),
  });

export const useRefreshPackageAccess = () => {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.servicePackages() }),
      client.invalidateQueries({ queryKey: queryKeys.adminRoleCatalog() }),
      client.invalidateQueries({ queryKey: queryKeys.me() }),
      client.invalidateQueries({ queryKey: queryKeys.scopes() }),
      client.invalidateQueries({ queryKey: ['integrations'] }),
      client.invalidateQueries({ queryKey: ['capabilities'] }),
    ]);
  };
};

export const useServicePackagesLogic = () => {
  const [params, setParams] = useSearchParams();
  const catalog = usePackageCatalog();
  const tenants = useQuery({
    queryKey: ['tenants'],
    queryFn: () => apiRequest<Tenant[]>('/api/tenants'),
  });
  const tenantId = params.get('tenant') ?? tenants.data?.[0]?.id ?? null;
  const assignments = useQuery({
    queryKey: queryKeys.tenantServicePackages(tenantId ?? ''),
    queryFn: () =>
      apiRequest<PackageAssignment[]>(`/api/tenants/${tenantId ?? ''}/service-packages`),
    enabled: !!tenantId,
  });
  const refresh = useRefreshPackageAccess();
  const mutation = useMutation({
    // Capture the tenant in the mutation variables; changing the selector while
    // a request is in flight must never redirect that write to another tenant.
    mutationFn: ({
      tenantId: target,
      packageId,
      enabled,
    }: {
      tenantId: string;
      packageId: string;
      enabled: boolean;
    }) =>
      apiRequest<PackageAssignment>(`/api/tenants/${target}/service-packages/${packageId}`, {
        method: 'PUT',
        body: { enabled },
      }),
    onSuccess: refresh,
  });
  return {
    catalog,
    tenants,
    assignments,
    tenantId,
    mutation,
    selectTenant: (id: string) =>
      setParams((previous) => {
        const next = new URLSearchParams(previous);
        if (id === tenants.data?.[0]?.id) next.delete('tenant');
        else next.set('tenant', id);
        return next;
      }),
    setEnabled: (packageId: string, enabled: boolean) => {
      if (tenantId) mutation.mutate({ tenantId, packageId, enabled });
    },
  };
};
